import { IonPage } from '../components/ionic';
import { initials, normalizeRoomCode, sanitizeDisplayName, type RoomPreview } from '@calling/shared';
import { lazy, Suspense, useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CallSession, type FinishReason } from '../call/session';
import { VideoView } from '../components/VideoView';
import { CamIcon, CamOffIcon, MicIcon, MicOffIcon } from '../components/icons';
import { AgreeLine } from '../components/Shell';
import { Banner, Dialog, Loading } from '../components/ui';
import { activeCall } from '../lib/activeCall';
import { api, ApiError, NetworkError } from '../lib/api';
import { acquire, deviceLabel, listDevices, permissionNotice, supportsCalls, supportsSpeakerChoice, watchLevel, type Acquired, type DeviceLists } from '../lib/media';
import { APP_NAME, isNative, openAppSettings } from '../lib/platform';
import { usePasswordSignIn, useProviderNames } from '../lib/serverInfo';
import { devicePrefs, guestName } from '../lib/storage';
import { useSession, useStore } from '../lib/useStore';
import { ProviderButtons } from './SignIn';

// The call screen and the WebRTC stack load only once someone is admitted (E6-S9).
const CallScreen = lazy(() => import('../call/CallScreen'));

function Frame({ children, center = false }: { children: ReactNode; center?: boolean }) {
  return (
    <IonPage>
      <div className={`screen${center ? ' screen--center' : ''}`} style={{ height: '100%', overflowY: 'auto', width: '100%' }}>
        {children}
      </div>
    </IonPage>
  );
}

/** A full-screen message with up to two actions. */
function Notice({ title, body, actions, testId }: { title: string; body?: ReactNode; actions?: ReactNode; testId?: string }) {
  return (
    <Frame center>
      <div className="spacer" />
      <h1 style={{ fontSize: 24, fontWeight: 600 }} data-testid={testId ?? 'notice-title'}>
        {title}
      </h1>
      {body ? (
        <p className="muted" style={{ fontSize: 15, lineHeight: 1.5, maxWidth: 380 }}>
          {body}
        </p>
      ) : null}
      <div className="stack" style={{ width: '100%', maxWidth: 380, marginTop: 12 }}>
        {actions}
      </div>
      <div className="spacer" />
    </Frame>
  );
}

const HomeLink = ({ primary = false }: { primary?: boolean }) => (
  <Link to="/" className={`btn ${primary ? 'btn--primary' : 'btn--ghost'} btn--block`}>
    Return home
  </Link>
);

interface JoinChoices {
  name?: string;
  media: Acquired;
  micOn: boolean;
  camOn: boolean;
}

function Meter({ track, active }: { track: MediaStreamTrack | null; active: boolean }) {
  const [level, setLevel] = useState(0);
  useEffect(() => {
    if (!track || !active) {
      setLevel(0);
      return;
    }
    return watchLevel(track, setLevel);
  }, [track, active]);
  const heights = [6, 12, 18, 10, 4];
  const lit = Math.round(level * 5);
  return (
    <div className="meter" role="meter" aria-label="Microphone level" aria-valuemin={0} aria-valuemax={5} aria-valuenow={lit} data-testid="mic-meter" data-level={lit}>
      {heights.map((h, i) => (
        <i key={i} data-on={i < lit} style={{ height: i < lit ? h + 4 : h }} />
      ))}
    </div>
  );
}

function PreJoin({ preview, userName, suggestedName, onJoin }: { preview: RoomPreview; userName: string | null; suggestedName?: string; onJoin: (choices: JoinChoices) => void }) {
  const prefs = useRef(devicePrefs.get()).current;
  const [media, setMedia] = useState<Acquired | null>(null);
  const [micOn, setMicOn] = useState(prefs.micOn);
  const [camOn, setCamOn] = useState(prefs.camOn);
  const [devices, setDevices] = useState<DeviceLists>({ mics: [], cams: [], speakers: [] });
  const [selected, setSelected] = useState({ micId: prefs.micId ?? '', camId: prefs.camId ?? '', speakerId: prefs.speakerId ?? '' });
  const [name, setName] = useState(suggestedName ?? guestName.get());
  const viaHostLink = suggestedName !== undefined;
  const [nameError, setNameError] = useState('');
  const [signIn, setSignIn] = useState(false);
  const signInWith = useProviderNames();
  const password = usePasswordSignIn();
  const [busy, setBusy] = useState(false);
  const mediaRef = useRef<Acquired | null>(null);
  const handedOver = useRef(false);
  const guest = userName === null;
  // Signed in without leaving the page (an email and password, or the iPhone's Sign in with Apple sheet): the sign-in dialog has done its job.
  useEffect(() => {
    if (!guest) setSignIn(false);
  }, [guest]);

  const stop = (m: Acquired | null) => {
    m?.audioTrack?.stop();
    m?.videoTrack?.stop();
  };

  const requestSeq = useRef(0);
  const request = useCallback(async (wantVideo: boolean, ids: { micId?: string; camId?: string }) => {
    const seq = ++requestSeq.current;
    setBusy(true);
    stop(mediaRef.current);
    mediaRef.current = null;
    // A 320×240 preview keeps weak devices cool (E3-S1).
    const got = await acquire({ wantAudio: true, wantVideo, micId: ids.micId || undefined, camId: ids.camId || undefined, preview: true });
    // A newer request, or leaving the screen, makes this result stale.
    if (seq !== requestSeq.current || handedOver.current) return stop(got);
    mediaRef.current = got;
    setMedia(got);
    setBusy(false);
    const lists = await listDevices();
    if (seq === requestSeq.current) setDevices(lists);
  }, []);

  useEffect(() => {
    void request(prefs.camOn, prefs);
    return () => {
      requestSeq.current++;
      if (!handedOver.current) stop(mediaRef.current);
      mediaRef.current = null;
    };
    // Runs once: the preview is requested when the screen opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A headset plugged in or pulled out changes the lists.
  useEffect(() => {
    const refresh = () => void listDevices().then(setDevices);
    navigator.mediaDevices?.addEventListener?.('devicechange', refresh);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', refresh);
  }, []);

  const toggleCamera = async () => {
    const next = !camOn;
    setCamOn(next);
    devicePrefs.set({ camOn: next });
    if (!next) {
      media?.videoTrack?.stop();
      const updated = media ? { ...media, videoTrack: null, cam: 'off' as const } : media;
      mediaRef.current = updated;
      setMedia(updated);
    } else {
      await request(true, selected);
    }
  };

  const toggleMic = () => {
    setMicOn(!micOn);
    devicePrefs.set({ micOn: !micOn });
  };

  const choose = async (kind: 'micId' | 'camId' | 'speakerId', value: string) => {
    const next = { ...selected, [kind]: value };
    setSelected(next);
    devicePrefs.set({ [kind]: value || undefined });
    if (kind !== 'speakerId') await request(camOn, next);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    let cleanName: string | undefined;
    if (guest) {
      const clean = sanitizeDisplayName(name);
      if (!clean) return setNameError(viaHostLink ? 'Enter your name so people know who is hosting.' : 'Enter your name so the host knows who is asking to join.');
      if (!viaHostLink) guestName.set(clean);
      cleanName = clean;
    }
    if (!media) return;
    handedOver.current = true;
    onJoin({ name: cleanName, media, micOn: micOn && media.mic === 'ok', camOn: camOn && media.cam === 'ok' });
  };

  const displayName = guest ? name.trim() || 'You' : userName;
  const notice = media ? permissionNotice(media.mic, media.cam, isNative, APP_NAME) : null;
  const micBlocked = media !== null && media.mic !== 'ok';
  const camBlocked = media !== null && camOn && media.cam !== 'ok';
  const supported = supportsCalls();

  if (!supported) {
    return (
      <Notice
        title="This browser can't make calls"
        body="Open this link in a current version of Chrome, Edge, Firefox or Safari, or in the mobile app."
        actions={<HomeLink />}
        testId="unsupported"
      />
    );
  }

  return (
    <Frame>
      <form onSubmit={submit} noValidate style={{ display: 'contents' }}>
        <div className="stack stack--tight" style={{ gap: 2 }}>
          <span className="mono muted" style={{ fontSize: 13 }}>
            {preview.code}
          </span>
          <h1 style={{ fontSize: 22, fontWeight: 600 }} data-testid="meeting-title">
            {preview.title}
          </h1>
          {!preview.isOwner ? (
            <span className="muted" style={{ fontSize: 13 }}>
              Hosted by {preview.hostName}
            </span>
          ) : null}
        </div>

        <div className="preview" data-testid="preview" data-camera={media?.videoTrack ? 'on' : 'off'}>
          {media?.videoTrack ? (
            <VideoView track={media.videoTrack} mirrored />
          ) : (
            <span className="avatar" style={{ width: 96, height: 96, background: '#4A6078', fontSize: 34 }} aria-hidden="true">
              {initials(displayName)}
            </span>
          )}
          <div className="preview__tag">{media?.videoTrack ? 'Preview' : busy ? 'Starting…' : 'Camera off'}</div>
          <div className="preview__controls">
            <Meter track={media?.audioTrack ?? null} active={micOn} />
            <div className="row">
              <button
                type="button"
                className="round-btn"
                data-off={!micOn || micBlocked}
                aria-pressed={micOn && !micBlocked}
                aria-label={micBlocked ? 'Microphone unavailable' : micOn ? 'Turn off microphone' : 'Turn on microphone'}
                onClick={toggleMic}
                disabled={micBlocked}
              >
                {micOn && !micBlocked ? <MicIcon /> : <MicOffIcon />}
                {micBlocked ? <span className="round-btn__warn" aria-hidden="true">!</span> : null}
              </button>
              <button
                type="button"
                className="round-btn"
                data-off={!camOn || camBlocked}
                aria-pressed={camOn && !camBlocked}
                aria-label={camOn && !camBlocked ? 'Turn off camera' : 'Turn on camera'}
                onClick={toggleCamera}
                disabled={busy}
              >
                {camOn && !camBlocked ? <CamIcon /> : <CamOffIcon />}
                {camBlocked ? <span className="round-btn__warn" aria-hidden="true">!</span> : null}
              </button>
            </div>
          </div>
        </div>

        {preview.recording ? <div className="rec-note">This meeting is being recorded</div> : null}
        {preview.willRecord ? <div className="rec-note">This meeting will be recorded</div> : null}
        {preview.locked && !preview.isOwner ? (
          <Banner tone="info" title="This meeting is locked">
            The host is not accepting new people right now. You can try again later.
          </Banner>
        ) : null}

        {notice ? (
          <Banner
            tone={notice.tone}
            title={notice.title}
            testId="permission-notice"
            action={
              notice.action === 'openSettings' ? (
                <button type="button" className="btn" onClick={() => void openAppSettings()}>
                  Open Settings
                </button>
              ) : notice.action === 'retry' ? (
                <button type="button" className="btn" disabled={busy} onClick={() => void request(camOn, selected)}>
                  Try again
                </button>
              ) : undefined
            }
          >
            {notice.body}
          </Banner>
        ) : null}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
          <select className="select" aria-label="Microphone" value={selected.micId} disabled={devices.mics.length === 0} onChange={(e) => void choose('micId', e.target.value)}>
            <option value="">Mic: Default</option>
            {devices.mics.map((d, i) => (
              <option key={d.deviceId} value={d.deviceId}>
                Mic: {deviceLabel(d, i, 'Microphone')}
              </option>
            ))}
          </select>
          <select className="select" aria-label="Camera" value={selected.camId} disabled={devices.cams.length === 0} onChange={(e) => void choose('camId', e.target.value)}>
            <option value="">Camera: Default</option>
            {devices.cams.map((d, i) => (
              <option key={d.deviceId} value={d.deviceId}>
                Camera: {deviceLabel(d, i, 'Camera')}
              </option>
            ))}
          </select>
          {supportsSpeakerChoice() && devices.speakers.length > 0 ? (
            <select className="select" aria-label="Speaker" style={{ gridColumn: '1 / -1' }} value={selected.speakerId} onChange={(e) => void choose('speakerId', e.target.value)}>
              <option value="">Audio: Default speaker</option>
              {devices.speakers.map((d, i) => (
                <option key={d.deviceId} value={d.deviceId}>
                  Audio: {deviceLabel(d, i, 'Speaker')}
                </option>
              ))}
            </select>
          ) : null}
        </div>

        {guest ? (
          <div className="field">
            <label htmlFor="name">Your name</label>
            <input
              id="name"
              className="input"
              value={name}
              maxLength={40}
              autoComplete="name"
              aria-invalid={nameError ? true : undefined}
              aria-describedby="name-hint"
              onChange={(e) => {
                setName(e.target.value);
                setNameError('');
              }}
            />
            {nameError ? (
              <span className="error" role="alert">
                {nameError}
              </span>
            ) : null}
            <span className="hint" id="name-hint">
              {viaHostLink ? 'You’re the host of this meeting. People who ask to join wait until you admit them.' : 'You’re joining as a guest. The host will see a Guest label.'}
            </span>
          </div>
        ) : (
          <p className="muted" style={{ fontSize: 14 }} data-testid="joining-as">
            Joining as <b style={{ color: 'var(--text)' }}>{userName}</b>
            {preview.isOwner ? ' · you are the host' : ''}
          </p>
        )}

        <div className="spacer" />
        <button className="btn btn--primary btn--block" style={{ height: 54, fontSize: 17 }} disabled={media === null}>
          {preview.isOwner ? 'Join now' : 'Ask to join'}
        </button>
        {/* Joining accepts the Terms, as signing in does (Google Play's user-generated content policy). */}
        <AgreeLine action="joining" />
        {guest && !viaHostLink ? (
          <div className="row" style={{ fontSize: 13 }}>
            <button type="button" className="link-btn" onClick={() => setSignIn(true)}>
              Sign in instead
            </button>
          </div>
        ) : null}
      </form>

      {signIn ? (
        <Dialog title="Sign in" onClose={() => setSignIn(false)}>
          <p>
            {password
              ? `Sign in to join with your own name, ${signInWith ? `with the ${signInWith} account you already have or ` : ''}with your email address and password. You will come straight back to this meeting.`
              : `Sign in to join with your own name. Use ${signInWith ? `the ${signInWith} account` : 'an account'} you already have; there is no new password to create. You will come straight back to this meeting.`}
          </p>
          {/* An email and password sign-in happens in the dialog: it closes, and the preview starts again as the person signed in. */}
          <ProviderButtons returnTo={`/m/${preview.code}`} onSignedIn={() => setSignIn(false)} />
        </Dialog>
      ) : null}
    </Frame>
  );
}

function Waiting({ call, title, guest, onCancel }: { call: CallSession; title: string; guest: boolean; onCancel: () => void }) {
  const state = useStore(call);
  const name = call.displayName;
  return (
    <Frame center>
      <div className="row row--between" style={{ alignSelf: 'stretch' }}>
        <span style={{ fontSize: 15, fontWeight: 600 }}>{title}</span>
        <span className="pill" data-tone={state.connection === 'connected' ? undefined : 'warn'} role="status">
          {state.connection === 'connected' ? 'Connected' : 'Reconnecting…'}
        </span>
      </div>
      <div className="waiting-ring">
        {state.local.cameraTrack ? (
          <div>
            <VideoView track={state.local.cameraTrack} mirrored />
          </div>
        ) : (
          <div style={{ background: '#2A3A4A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 52, fontWeight: 600 }} aria-hidden="true">
            {initials(name)}
          </div>
        )}
      </div>
      <div className="stack stack--tight" style={{ alignItems: 'center', padding: '0 12px', gap: 8 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600 }} data-testid="waiting-title">
          {state.phase === 'joining' ? 'Connecting…' : state.hostPresent ? 'Asking to be let in…' : 'Waiting for the host to start the meeting'}
        </h1>
        <p className="muted" style={{ fontSize: 15, lineHeight: 1.5 }}>
          {state.hostPresent
            ? 'You’ll join when the host admits you. Your camera and mic aren’t shared until then.'
            : 'You’ll ask to join automatically as soon as the host arrives. Your camera and mic aren’t shared until you are admitted.'}
        </p>
      </div>
      <div className="row" style={{ flexWrap: 'wrap', justifyContent: 'center' }}>
        <span className="chip">{state.local.cameraTrack ? 'Camera on' : 'Camera off'}</span>
        <span className="chip">{state.local.micOn ? 'Mic on' : 'Mic off'}</span>
        <span className="chip">{guest ? 'Guest' : 'Signed in'}</span>
      </div>
      <div className="spacer" />
      {state.hostPresent ? <div className="note" style={{ alignSelf: 'stretch' }}>If no one responds in 5 minutes, you can ask again.</div> : null}
      <button className="btn btn--ghost btn--block" onClick={() => void call.cancel().then(onCancel)}>
        Cancel
      </button>
    </Frame>
  );
}

const FINISHED: Record<FinishReason, { title: string; body?: string; again?: string }> = {
  left: { title: 'You left the meeting', again: 'Rejoin' },
  ended: { title: 'This meeting has ended' },
  timeLimit: { title: 'This meeting reached its time limit', again: 'Rejoin' },
  removed: { title: 'You were removed from the meeting', body: 'The host removed you. You can’t rejoin this meeting.' },
  replaced: { title: 'You joined from another window', body: 'This meeting is now open in another tab or on another device with the same account.', again: 'Use this window instead' },
  lost: { title: 'Connection lost', body: 'We tried to reconnect for a minute without success. Check your network, then rejoin. You won’t need to be admitted again.', again: 'Rejoin' },
  denied: { title: "You can't join this meeting", body: 'The host declined your request.' },
  blocked: { title: "You can't join this meeting", body: 'The host has declined your requests for this meeting.' },
  locked: { title: 'This meeting is locked', body: 'The host is not accepting new people right now.', again: 'Try again' },
  full: { title: 'The waiting room is full', body: 'Too many people are waiting to join. Try again in a few minutes.', again: 'Try again' },
  timeout: { title: 'No one responded', body: 'The host did not respond to your request within five minutes.', again: 'Ask again' },
  cancelled: { title: 'Cancelled by host', body: 'The host cancelled this meeting.' },
  expired: { title: 'This meeting has expired', body: 'The link was not used for 30 days. Ask the host for a new one.' },
  unavailable: { title: 'This meeting isn’t available', body: 'You can’t join it with this link now. Check with whoever sent it to you.' },
  notFound: { title: 'This meeting link is not valid', body: 'Check the link and try again.' },
  rateLimited: { title: 'Too many attempts', body: 'Wait a minute, then try again.', again: 'Try again' },
  error: { title: "Couldn't join the meeting", again: 'Try again' },
};

export default function Meeting() {
  const { code: raw = '' } = useParams();
  const code = normalizeRoomCode(raw);
  const session = useSession();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<RoomPreview | null>(null);
  const [problem, setProblem] = useState<{ kind: 'notFound' | 'unavailable' | 'blocked' | 'network' | 'other'; message: string } | null>(null);
  const [call, setCall] = useState<CallSession | null>(null);
  const [attempt, setAttempt] = useState(0);
  const callRef = useRef<CallSession | null>(null);
  const userId = session.user?.id ?? null;
  // A host link (?host=…) lets the organiser host without an account. The
  // token is kept for this tab and taken out of the address bar, so copying
  // the address never hands out host rights.
  const [hostToken] = useState<string | null>(() => {
    if (!code) return null;
    const key = `calling.host.${code}`;
    const fromUrl = new URLSearchParams(window.location.search).get('host');
    try {
      if (fromUrl) sessionStorage.setItem(key, fromUrl);
      return fromUrl ?? sessionStorage.getItem(key);
    } catch {
      return fromUrl;
    }
  });
  useEffect(() => {
    if (new URLSearchParams(window.location.search).has('host')) navigate(window.location.pathname, { replace: true });
  }, [navigate]);

  // A meeting link typed with capitals or without dashes still works.
  useEffect(() => {
    if (code && code !== raw) navigate(`/m/${code}`, { replace: true });
  }, [code, raw, navigate]);

  useEffect(() => {
    if (!code || session.status === 'loading') return;
    let cancelled = false;
    setPreview(null);
    setProblem(null);
    api
      .request<{ room: RoomPreview }>(`/api/rooms/${code}${hostToken ? `?host=${encodeURIComponent(hostToken)}` : ''}`, { auth: 'optional' })
      .then((res) => !cancelled && setPreview(res.room))
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) setProblem({ kind: 'notFound', message: err.message });
        else if (err instanceof ApiError && err.code === 'unavailable') setProblem({ kind: 'unavailable', message: err.message });
        else if (err instanceof ApiError && err.status === 429) setProblem({ kind: 'blocked', message: 'Too many attempts from this network. Try again later.' });
        else if (err instanceof NetworkError) setProblem({ kind: 'network', message: err.message });
        else setProblem({ kind: 'other', message: err instanceof Error ? err.message : 'Something went wrong.' });
      });
    return () => {
      cancelled = true;
    };
  }, [code, session.status, userId, attempt, hostToken]);

  // Leaving the page, by any route, leaves the meeting and frees the camera.
  useEffect(() => {
    return () => callRef.current?.dispose();
  }, []);

  const begin = useCallback(
    (choices: JoinChoices) => {
      const next = new CallSession({
        code: code!,
        name: choices.name,
        audioTrack: choices.media.audioTrack,
        videoTrack: choices.media.videoTrack,
        mic: choices.media.mic,
        cam: choices.media.cam,
        micOn: choices.micOn,
        camOn: choices.camOn,
        displayName: choices.name ?? session.user?.name ?? 'You',
        hostToken: hostToken ?? undefined,
      });
      callRef.current?.dispose();
      callRef.current = next;
      setCall(next);
      void next.start();
    },
    [code, session.user?.name, hostToken],
  );

  /** Joins again with the same devices as before, without going back through the preview. */
  const rejoin = useCallback(async () => {
    const prefs = devicePrefs.get();
    const media = await acquire({ wantAudio: true, wantVideo: prefs.camOn, micId: prefs.micId, camId: prefs.camId });
    const name = session.user ? undefined : callRef.current?.displayName || guestName.get() || undefined;
    begin({ name, media, micOn: prefs.micOn && media.mic === 'ok', camOn: prefs.camOn && media.cam === 'ok' });
  }, [begin, session.user]);

  const reset = () => {
    callRef.current?.dispose();
    callRef.current = null;
    setCall(null);
    setAttempt((n) => n + 1);
  };

  if (!code) return <Notice title="This meeting link is not valid" body="Check the link and try again." actions={<HomeLink primary />} />;
  if (call) return <ActiveCall call={call} preview={preview} guest={!session.user} onReset={reset} onRejoin={() => void rejoin()} />;
  if (problem) {
    if (problem.kind === 'notFound') return <Notice title="This meeting link is not valid" body="Check the link and try again." actions={<HomeLink primary />} />;
    if (problem.kind === 'unavailable') return <Notice title={FINISHED.unavailable.title} body={FINISHED.unavailable.body} actions={<HomeLink primary />} testId="meeting-unavailable" />;
    return (
      <Notice
        title={problem.kind === 'network' ? "Can't reach the server" : problem.kind === 'blocked' ? 'Too many attempts' : 'Something went wrong'}
        body={problem.message}
        actions={
          <>
            <button className="btn btn--primary btn--block" onClick={() => setAttempt((n) => n + 1)}>
              Try again
            </button>
            <HomeLink />
          </>
        }
      />
    );
  }
  if (!preview || session.status === 'loading') return <Loading label="Opening meeting…" />;
  if (preview.status === 'cancelled') return <Notice title="Cancelled by host" body="The host cancelled this meeting." actions={<HomeLink primary />} />;
  if (preview.status === 'ended') return <Notice title="This meeting has ended" actions={<HomeLink primary />} />;
  if (preview.status === 'expired') return <Notice title="This meeting has expired" body="The link was not used for 30 days. Ask the host for a new one." actions={<HomeLink primary />} />;

  // Someone using a host link has no profile: they confirm the name the meeting was booked under.
  const viaHostLink = hostToken !== null && preview.isOwner && !session.user;
  return <PreJoin key={userId ?? 'guest'} preview={preview} userName={session.user?.name ?? null} suggestedName={viaHostLink ? preview.hostName : undefined} onJoin={begin} />;
}

function ActiveCall({ call, preview, guest, onReset, onRejoin }: { call: CallSession; preview: RoomPreview | null; guest: boolean; onReset: () => void; onRejoin: () => void }) {
  const phase = useStore(call).phase;
  const finish = useStore(call).finish;
  // From asking to join until the call ends, a link opened from another app asks before leaving it (App.tsx).
  const live = phase !== 'finished';
  const code = preview?.code;
  const title = preview?.title;
  useEffect(() => {
    if (!live || !code) return;
    activeCall.set({ code, title: title ?? 'this meeting' });
    return () => activeCall.clear(code);
  }, [live, code, title]);

  if (phase === 'finished' && finish) {
    const copy = FINISHED[finish.reason];
    const title = finish.reason === 'ended' && finish.message ? finish.message : copy.title;
    // The person whose plan set the limit is told what it is; everyone else, only that there is one.
    const limited = finish.reason === 'timeLimit';
    const ownLimit = limited && finish.owner === true;
    const body = limited
      ? ownLimit
        ? `${finish.minutes ? `Your plan allows meetings of up to ${finish.minutes} minutes.` : 'Your plan limits how long a meeting can run.'} You can start it again with the same link.`
        : 'It ran for as long as the host’s plan allows. If the host starts it again, you can rejoin with the same link.'
      : finish.reason === 'error' || finish.reason === 'full'
        ? (finish.message ?? copy.body)
        : finish.reason === 'removed' && finish.by
          ? `${finish.by} removed you. You can’t rejoin this meeting.`
          : copy.body;
    return (
      <Notice
        title={title}
        body={<span data-testid="finished-body">{body}</span>}
        testId="finished-title"
        actions={
          <>
            {copy.again ? (
              <button className="btn btn--primary btn--block" onClick={onRejoin}>
                {ownLimit ? 'Start again' : copy.again}
              </button>
            ) : null}
            {ownLimit && !isNative ? (
              <Link to="/billing" className="btn btn--block">
                See plans
              </Link>
            ) : null}
            <HomeLink primary={!copy.again} />
          </>
        }
      />
    );
  }
  if (phase === 'inCall') {
    return (
      <Suspense fallback={<Loading label="Joining…" />}>
        <CallScreen call={call} />
      </Suspense>
    );
  }
  // "Cancel" simply returns to the preview.
  return <Waiting call={call} title={preview?.title ?? 'Meeting'} guest={guest} onCancel={onReset} />;
}
